import { useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { bulkInvite, createInvite, type BulkInviteResult } from '../lib/advisorApi';
import './advisor.css';

const CSV_PLACEHOLDER = `email,fullName,customMessage
john@example.com,John Chen,
maria@example.com,Maria Okonkwo,Thought of you after our call.`;

export function InvitesScreen() {
  const { api } = useAuth();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState<{ inviteUrl: string; expiresAt: string } | null>(null);

  const [attestOne, setAttestOne] = useState(false);

  const [csv, setCsv] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkInviteResult | null>(null);
  const [attestBulk, setAttestBulk] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendOne(): Promise<void> {
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      const res = await createInvite(api, {
        clientEmail: email.trim(),
        clientDisplayName: name.trim() === '' ? undefined : name.trim(),
        inviteMessage: message.trim() === '' ? undefined : message.trim(),
        advisorAttested: attestOne,
      });
      setSent({ inviteUrl: res.inviteUrl, expiresAt: res.expiresAt });
      setEmail('');
      setName('');
      setMessage('');
      setAttestOne(false);
    } catch {
      setError('That invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function sendBulk(): Promise<void> {
    setBusy(true);
    setError(null);
    setBulkResult(null);
    try {
      setBulkResult(await bulkInvite(api, csv, attestBulk));
    } catch {
      setError('That list could not be processed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">Bring a client in</p>
        <h1 className="page__title">Invite</h1>
        <p className="page__sub">
          An invitation is personal. The client signs up under your firm, and their vault is theirs
          from the first second — you hold no key to it.
        </p>
      </header>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      <div className="la-card formCard">
        <p className="la-label">One client</p>
        <h2>Send an invitation</h2>

        <label className="auth__label" htmlFor="invite-email">
          Client email
        </label>
        <input
          id="invite-email"
          className="field field--wide"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <label className="auth__label" htmlFor="invite-name">
          Their name
        </label>
        <input
          id="invite-name"
          className="field field--wide"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <label className="auth__label" htmlFor="invite-message">
          A line from you (optional)
        </label>
        <input
          id="invite-message"
          className="field field--wide"
          type="text"
          placeholder="Thought of you after our conversation."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ marginBottom: 20 }}
        />

        <label className="attest" htmlFor="invite-attest">
          <input
            id="invite-attest"
            type="checkbox"
            checked={attestOne}
            onChange={(e) => setAttestOne(e.target.checked)}
          />
          <span>
            I know this person. They are a client of this firm and this is the address we hold for
            them.
            <span className="attest__note">
              Leave this unticked and they will wait in your attestation queue instead.
            </span>
          </span>
        </label>

        <div className="la-cta-wrap">
          <button
            type="button"
            className="la-btn-primary"
            disabled={busy || email.trim() === ''}
            onClick={() => void sendOne()}
          >
            {busy ? 'Sending' : 'Send invitation'}
          </button>
        </div>

        {sent !== null && (
          <p className="note">
            Sent. The invitation link is live for 72 hours — if they lose the email, you can resend
            this to them directly:
            <br />
            <strong>{sent.inviteUrl}</strong>
          </p>
        )}
      </div>

      <div className="la-card formCard">
        <p className="la-label">Many clients</p>
        <h2>Invite from a list</h2>
        <p>
          Paste a CSV with the columns <code>email</code>, <code>fullName</code>, and optionally{' '}
          <code>customMessage</code>. Each row is invited individually — one bad row does not stop
          the rest.
        </p>

        <textarea
          className="field field--area"
          aria-label="CSV of clients to invite"
          placeholder={CSV_PLACEHOLDER}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />

        <label className="attest" htmlFor="invite-attest-bulk">
          <input
            id="invite-attest-bulk"
            type="checkbox"
            checked={attestBulk}
            onChange={(e) => setAttestBulk(e.target.checked)}
          />
          <span>
            I know every person on this list. They are clients of this firm and these are the
            addresses we hold for them.
            <span className="attest__note">
              Applies to each row. Leave it unticked and they will wait in your attestation queue
              instead.
            </span>
          </span>
        </label>

        <div className="la-cta-wrap" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="la-btn-primary"
            disabled={busy || csv.trim() === ''}
            onClick={() => void sendBulk()}
          >
            {busy ? 'Sending' : 'Send invitations'}
          </button>
        </div>

        {bulkResult !== null && (
          <div className="tableWrap" style={{ marginTop: 20 }}>
            <p className="note">
              {bulkResult.successCount} sent, {bulkResult.failureCount} failed, out of{' '}
              {bulkResult.totalRows}.
            </p>
            {bulkResult.failureCount > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Email</th>
                    <th>Why it failed</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResult.results
                    .filter((r) => !r.ok)
                    .map((r) => (
                      <tr key={`${r.row}-${r.email}`}>
                        <td>{r.row}</td>
                        <td className="table__name">{r.email}</td>
                        <td>{r.error ?? 'Unknown'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
